import type { ConfigVariable } from "@types"
import {
    ALARM_THRESHOLD_VARIABLE_GROUP_ORDER,
    getAlarmThresholdVariableGroupId,
    type AlarmThresholdVariableGroupId,
} from "@modules/energy-manager/constants/alarme"

/**
 * Option d'autocomplétion pour l'ajout d'un seuil d'alarme (équivalent legacy ProfileFormAdd).
 */
export type AlarmThresholdVariableOption = {
    name: string
    value: string
    icon?: string
    color?: string
}

/**
 * Groupe d’options pour PrimeVue AutoComplete (`optionGroupLabel` / `optionGroupChildren`).
 */
export type AlarmThresholdVariableOptionGroup = {
    label: string
    items: AlarmThresholdVariableOption[]
}

const EXCLUDE_VARIABLE_TYPES: readonly string[] = [
    "longitude_position_source",
    "latitude_position_source",
]

/**
 * Profils côté API variable (MongoDB enum: journalier | horaire) et libellés legacy.
 * @see homeys-api-metier variable.model.js — `profils` n’utilise pas `quotidien`.
 */
export const PROFIL_TO_THRESHOLD_FREQ: Record<string, "hourly" | "daily"> = {
    horaire: "hourly",
    journalier: "daily",
    quotidien: "daily",
    hourly: "hourly",
    daily: "daily",
}

/**
 * Liste ordonnée des fréquences seuils distinctes pour une variable config (horaire en premier si les deux).
 *
 * @param profils clés `profils` MongoDB de la variable
 * @returns tableau sans doublon, ordre horaire puis quotidien quand les deux existent
 */
export function getDistinctAlarmThresholdFreqsFromProfils(
    profils: string[] | undefined
): ("hourly" | "daily")[] {
    const raw = profils ?? []
    const seen = new Set<"hourly" | "daily">()
    const out: ("hourly" | "daily")[] = []
    for (const p of raw) {
        const freq = PROFIL_TO_THRESHOLD_FREQ[p]
        if (!freq || seen.has(freq)) continue
        seen.add(freq)
        out.push(freq)
    }
    return out.sort((a, b) => {
        if (a === b) return 0
        if (a === "hourly") return -1
        return 1
    })
}

/**
 * Fréquence par défaut pour auto-génération / formulaire : aligné sur `AlarmFormAdd`
 * (si horaire et quotidien disponibles, privilégier l’horaire).
 *
 * @param variable config variable métier
 * @returns null si aucun profil exploitable
 */
export function getDefaultAlarmThresholdFreqForVariable(
    variable: ConfigVariable | null | undefined
): "hourly" | "daily" | null {
    const options = getDistinctAlarmThresholdFreqsFromProfils(variable?.profils)
    if (options.length === 0) return null
    if (options.length === 1) return options[0]!
    return "hourly"
}

/**
 * Autre fréquence disponible pour un second essai (ex. quotidien après échec horaire).
 *
 * @param used fréquence déjà tentée
 * @param available fréquences supportées par la variable
 * @returns l’autre fréquence si présente, sinon null
 */
export function getAlternateAlarmThresholdFreq(
    used: "hourly" | "daily",
    available: readonly ("hourly" | "daily")[]
): "hourly" | "daily" | null {
    const other: "hourly" | "daily" = used === "hourly" ? "daily" : "hourly"
    return available.includes(other) ? other : null
}

type BuildAlarmThresholdVariablesOptionsParams = {
    variables: ConfigVariable[]
    existingProfilKeys: string[]
    allowedTypes: readonly string[]
    /** Exclure les variables dont chart.raw vaut "period" (page bâtiment legacy). */
    excludePeriodRaw?: boolean
    /**
     * Filtre optionnel sur le type de variable (ex. données présentes dans
     * `monitoring.firstdate` pour la source ou les sources du bâtiment).
     */
    shouldIncludeVariableType?: (variableType: string) => boolean
}

/**
 * Construit la liste des variables proposées à l'ajout d'un seuil (mêmes règles que homeys-front alarme).
 *
 * @param params paramètres de filtrage
 * @returns options triées par nom affiché
 */
export function buildAlarmThresholdVariablesOptions(
    params: BuildAlarmThresholdVariablesOptionsParams
): AlarmThresholdVariableOption[] {
    const {
        variables,
        existingProfilKeys,
        allowedTypes,
        excludePeriodRaw,
        shouldIncludeVariableType,
    } = params
    const used = new Set(existingProfilKeys)
    const allowed = new Set(allowedTypes)

    return variables
        .filter((v) => (v.profils?.length ?? 0) > 0)
        .filter((v) => !used.has(v.type))
        .filter((v) => !EXCLUDE_VARIABLE_TYPES.includes(v.type))
        .filter((v) => allowed.has(v.type))
        .filter((v) => !excludePeriodRaw || v.chart?.raw !== "period")
        .filter(
            (v) =>
                !shouldIncludeVariableType || shouldIncludeVariableType(v.type)
        )
        .map((v) => ({
            name: v.name,
            value: v.type,
            icon: v.icon,
            color: v.color,
        }))
        .sort((a, b) =>
            a.name.localeCompare(b.name, "fr", { sensitivity: "base" })
        )
}

/**
 * Regroupe les variables par famille pour le sélecteur (ordre des groupes défini dans les constantes).
 *
 * @param options liste plate déjà filtrée / triée
 * @param getGroupLabel libellé affiché pour chaque identifiant de groupe
 * @returns groupes non vides, dans l’ordre métier
 */
export function groupAlarmThresholdVariableOptions(
    options: AlarmThresholdVariableOption[],
    getGroupLabel: (groupId: AlarmThresholdVariableGroupId) => string
): AlarmThresholdVariableOptionGroup[] {
    const byGroup = new Map<
        AlarmThresholdVariableGroupId,
        AlarmThresholdVariableOption[]
    >()
    for (const id of ALARM_THRESHOLD_VARIABLE_GROUP_ORDER) {
        byGroup.set(id, [])
    }
    byGroup.set("other", [])

    for (const opt of options) {
        const gid = getAlarmThresholdVariableGroupId(opt.value)
        const list = byGroup.get(gid) ?? byGroup.get("other")!
        list.push(opt)
    }

    const result: AlarmThresholdVariableOptionGroup[] = []
    for (const id of ALARM_THRESHOLD_VARIABLE_GROUP_ORDER) {
        const items = byGroup.get(id)
        if (items?.length) {
            result.push({ label: getGroupLabel(id), items })
        }
    }
    const other = byGroup.get("other")
    if (other?.length) {
        result.push({ label: getGroupLabel("other"), items: other })
    }
    return result
}
